import { inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import {
  Observable,
  catchError,
  concatMap,
  filter,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs'

import {
  CreateDataMapperPayload,
  DataMapper,
  DataMappingType,
  SliceType,
  SourceElementType,
  UpdateDataMapperPayload,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import {
  MapperSaveBody,
  mapperSaveBody,
} from '@flowx/designer/modules/data-mapping/pending-attributes.util'
import {
  ActionHandler,
  ActionParamType,
} from '@flowx/designer/modules/node-actions/components/action-edit/action-edit.dictionary'
import { ApplicationResourceType } from '@flowx/designer/shared/entities/application.entity'
import { ApplicationApiParameters } from '@flowx/designer/shared/services/url-parameters-store.service'

import { IError } from '../../api/api-http.service'
import { DataMappingApiService } from '../../api/data-mapping.api'
import {
  NotificationTemplateDataModelEntityToUIEntity,
  ProcessDataModelEntityToUIEntity,
  WorkflowDataModelEntityToUIEntity,
} from '../../entities/data-model.entity'
import { ToastMessageService } from '../../services/toast-message.service'
import { DataMappingActions, LocalAttributeStaging } from '../actions/data-mapping.actions'
import * as NodeActionActions from '../actions/node-action.actions'
import * as NodeDataStreamsActions from '../actions/node-data-streams.actions'
import { ProcessDataModelActions } from '../actions/process-data-model.actions'
import {
  selectActionDataMappingState,
  selectLocalDataMappers,
  selectResourceNodesSlices,
} from '../selectors/data-mapping.selectors'
import { selectStableRouteParam } from '../selectors/router.selectors'

const selectProcessResourceId = selectStableRouteParam('selectedProcessResourceId')

export const loadResourceNodesSlices$ = createEffect(
  (
    actions$ = inject(Actions),
    dataMappingApiService = inject(DataMappingApiService),
    toastMessageService = inject(ToastMessageService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(DataMappingActions.loadResourceNodesSlices),
      withLatestFrom(store.select(selectResourceNodesSlices)),
      concatMap(([{ resourceId, resourceType, application, sliceType }]) =>
        getNodesSlicesResponse(
          resourceType,
          resourceId,
          application,
          dataMappingApiService,
          toastMessageService,
          sliceType
        )
      )
    ),
  { functional: true }
)

export const loadSelectedElementDataMappings$ = createEffect(
  (
    actions$ = inject(Actions),
    dataMappingApiService = inject(DataMappingApiService),
    toastMessageService = inject(ToastMessageService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(DataMappingActions.loadMappingsForSelectedElement),
      withLatestFrom(store.select(selectActionDataMappingState)),
      switchMap(([{ elementFlowxUuid, elementType }, actionDataMappings]) => {
        if (!actionDataMappings.entities[elementFlowxUuid]) {
          return dataMappingApiService.getElementMappings(elementFlowxUuid, elementType).pipe(
            map(({ resourceContent: actionMappings }) =>
              DataMappingActions.loadMappingsSuccess({
                elementFlowxUuid,
                targetProcessNodesMappers: actionMappings.mappers,
              })
            ),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage(error.title ?? `Loading data mappings failed`)
              return of(
                DataMappingActions.loadMappingsFailure({
                  elementFlowxUuid,
                })
              )
            })
          )
        } else {
          return of(DataMappingActions.cancelLoadMappingsForSelectedElement())
        }
      })
    ),
  { functional: true }
)

export const createMappingsOnActionSave$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(NodeActionActions.updateActionSuccess),
      withLatestFrom(store.select(selectLocalDataMappers)),
      map(([{ action, oldAction }, dataMappers]) => {
        const filteredMappers = [
          ActionHandler.START_INTEGRATION_WORKFLOW,
          ActionHandler.SEND_NOTIFICATION,
        ].includes(action.handlerService)
          ? dataMappers.filter((mapper) => mapper.type === DataMappingType.INPUT)
          : dataMappers

        const oldCollectionKey = oldAction?.actionParamsMap?.[ActionParamType.COLLECTION_KEY]?.value
        const newCollectionKey = action.actionParamsMap?.[ActionParamType.COLLECTION_KEY]?.value

        const inputKeyChanged =
          action.handlerService === ActionHandler.START_SUB_PROCESS &&
          oldCollectionKey != null &&
          newCollectionKey != null &&
          oldCollectionKey !== newCollectionKey

        const outputKeyChanged =
          action.handlerService === ActionHandler.START_SUB_PROCESS &&
          oldAction?.actionParamsMap?.[ActionParamType.OUTPUT_KEY]?.value !==
            action.actionParamsMap?.[ActionParamType.OUTPUT_KEY]?.value

        const keyChanged = inputKeyChanged || outputKeyChanged

        const createRequests = keyChanged
          ? filteredMappers
              .filter(
                (mapper) =>
                  mapper.elementFlowxUuid === action.flowxUuid &&
                  (mapper.sourceSlice?.sliceItems?.length || mapper.mapping?.length)
              )
              .map(({ flowxUuid, id, ...rest }) => rest)
          : filteredMappers.filter(
              (mapper) =>
                !mapper.flowxUuid &&
                (mapper.sourceSlice?.sliceItems?.length || mapper.mapping?.length)
            )

        const resourceType = {
          [ActionHandler.START_SUB_PROCESS]: ApplicationResourceType.Process,
          [ActionHandler.START_INTEGRATION_WORKFLOW]: ApplicationResourceType.Workflow,
          [ActionHandler.SEND_NOTIFICATION]: ApplicationResourceType.NotificationTemplate,
        }[action.handlerService]

        return DataMappingActions.bulkCreateDataMappings({
          elementFlowxUuid: action.flowxUuid,
          elementType: SourceElementType.ACTION,
          resourceType,
          actionMappers: createRequests,
        })
      })
    ),
  { functional: true }
)
export const createMappingsOnDataStreamNodeValueSave$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(NodeDataStreamsActions.updateDataStreamsSuccess),
      withLatestFrom(store.select(selectLocalDataMappers)),
      mergeMap(([{ nodeDataStreams }, dataMappers]) =>
        nodeDataStreams.map((dataStream) => {
          const createRequests = dataMappers.filter(
            (mapper) =>
              !mapper.flowxUuid &&
              mapper.type === DataMappingType.OUTPUT &&
              (dataStream?.flowxUuid === mapper.elementFlowxUuid ||
                dataStream?.tempUuid === mapper.elementFlowxUuid) &&
              (mapper.sourceSlice?.sliceItems?.length || mapper.mapping?.length)
          )
          return DataMappingActions.bulkCreateDataMappings({
            elementFlowxUuid: dataStream.flowxUuid,
            tempUuid: dataStream.tempUuid,
            elementType: SourceElementType.DATA_STREAM_NODE_VALUE,
            resourceType: ApplicationResourceType.Workflow,
            actionMappers: createRequests,
          })
        })
      )
    ),
  { functional: true }
)

export const updateMappingsOnActionSave$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(NodeActionActions.updateActionSuccess),
      filter(({ action, oldAction }) => {
        if (action.handlerService === ActionHandler.START_SUB_PROCESS) {
          const oldInputKey = oldAction?.actionParamsMap?.[ActionParamType.COLLECTION_KEY]?.value
          const newInputKey = action.actionParamsMap?.[ActionParamType.COLLECTION_KEY]?.value
          const oldOutputKey = oldAction?.actionParamsMap?.[ActionParamType.OUTPUT_KEY]?.value
          const newOutputKey = action.actionParamsMap?.[ActionParamType.OUTPUT_KEY]?.value
          return oldInputKey === newInputKey && oldOutputKey === newOutputKey
        }
        return true
      }),
      withLatestFrom(store.select(selectLocalDataMappers)),
      map(([{ action }, dataMappers]) => {
        const filteredMappers = [
          ActionHandler.START_INTEGRATION_WORKFLOW,
          ActionHandler.SEND_NOTIFICATION,
        ].includes(action.handlerService)
          ? dataMappers.filter((mapper) => mapper.type === DataMappingType.INPUT)
          : dataMappers

        const updateRequests = filteredMappers.filter((mapper) => mapper.flowxUuid)
        const resourceType = {
          [ActionHandler.START_SUB_PROCESS]: ApplicationResourceType.Process,
          [ActionHandler.START_INTEGRATION_WORKFLOW]: ApplicationResourceType.Workflow,
          [ActionHandler.SEND_NOTIFICATION]: ApplicationResourceType.NotificationTemplate,
        }[action.handlerService]
        return DataMappingActions.bulkUpdateDataMappings({
          elementFlowxUuid: action.flowxUuid,
          resourceType,
          elementType: SourceElementType.ACTION,
          actionMappers: updateRequests,
        })
      })
    ),
  { functional: true }
)
export const updateMappingsOnDataStreamNodeValueSave$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(NodeDataStreamsActions.updateDataStreamsSuccess),
      withLatestFrom(store.select(selectLocalDataMappers)),
      mergeMap(([{ nodeDataStreams }, dataMappers]) =>
        nodeDataStreams.map((dataStream) => {
          const updateRequests = dataMappers.filter(
            (mapper) =>
              mapper.flowxUuid &&
              mapper.type === DataMappingType.OUTPUT &&
              (dataStream.flowxUuid ?? dataStream.tempUuid) === mapper.elementFlowxUuid
          )
          return DataMappingActions.bulkUpdateDataMappings({
            elementFlowxUuid: dataStream.flowxUuid,
            resourceType: ApplicationResourceType.Workflow,
            elementType: SourceElementType.DATA_STREAM_NODE_VALUE,
            actionMappers: updateRequests,
          })
        })
      )
    ),
  { functional: true }
)

const toMapperPayload = <T extends Partial<DataMapper> & LocalAttributeStaging>({
  pendingAttributes,
  sourceSlice,
  mapping,
  ...mapper
}: T): Partial<DataMapper> & Pick<MapperSaveBody, 'newSourceAttributes'> => {
  const body = mapperSaveBody({
    slice: sourceSlice?.sliceItems ?? [],
    mappings: mapping ?? [],
    pendingAttributes,
  })

  return {
    ...mapper,
    sourceSlice: { ...sourceSlice, sliceItems: body.sliceItems },
    mapping: body.mapping,
    newSourceAttributes: body.newSourceAttributes,
  }
}

/** The host data model gained attributes, so re-read it. */
const refreshDataModelAfterStaging = (
  store: Store,
  resourceId: string | undefined,
  actionMappers: LocalAttributeStaging[]
): void => {
  if (resourceId && actionMappers.some(({ pendingAttributes }) => pendingAttributes?.length)) {
    store.dispatch(ProcessDataModelActions.loadEntities({ resourceId }))
  }
}

export const bulkCreateDataMappings$ = createEffect(
  (
    actions$ = inject(Actions),
    dataMappingApiService = inject(DataMappingApiService),
    toastMessageService = inject(ToastMessageService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(DataMappingActions.bulkCreateDataMappings),
      withLatestFrom(store.select(selectProcessResourceId)),
      mergeMap(
        ([
          { elementFlowxUuid, tempUuid, elementType, resourceType, actionMappers },
          processResourceId,
        ]) => {
          const requests = actionMappers.map((mapper) =>
            dataMappingApiService.createActionMapping(
              elementFlowxUuid,
              resourceType,
              elementType,
              toMapperPayload(mapper) as CreateDataMapperPayload
            )
          )

          return forkJoin(requests).pipe(
            map((response) => {
              refreshDataModelAfterStaging(store, processResourceId, actionMappers)

              return DataMappingActions.bulkCreateDataMappingsSuccess({
                elementFlowxUuid: tempUuid ?? elementFlowxUuid,
                elementType,
                actionMappers: response.map(({ resourceContent }) => resourceContent),
                resourceType,
              })
            }),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage(error?.title ?? 'Create data mapping failed')
              return of(
                DataMappingActions.bulkCreateDataMappingsFailure({
                  elementFlowxUuid: tempUuid ?? elementFlowxUuid,
                  elementType: SourceElementType.ACTION,
                })
              )
            })
          )
        }
      )
    ),
  { functional: true }
)

export const bulkUpdateDataMappings$ = createEffect(
  (
    actions$ = inject(Actions),
    dataMappingApiService = inject(DataMappingApiService),
    toastMessageService = inject(ToastMessageService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(DataMappingActions.bulkUpdateDataMappings),
      withLatestFrom(store.select(selectProcessResourceId)),
      mergeMap(
        ([{ elementFlowxUuid, elementType, resourceType, actionMappers }, processResourceId]) => {
          const updateMappers = actionMappers.filter((mapper) => mapper.flowxUuid)

          const requests = updateMappers.map((mapper) =>
            dataMappingApiService.updateActionMapping(
              elementFlowxUuid,
              resourceType,
              elementType,
              toMapperPayload(mapper) as UpdateDataMapperPayload
            )
          )

          return forkJoin(requests).pipe(
            map((response) => {
              refreshDataModelAfterStaging(store, processResourceId, updateMappers)

              return DataMappingActions.bulkUpdateDataMappingsSuccess({
                elementFlowxUuid,
                resourceType,
                elementType: SourceElementType.ACTION,
                actionMappers: response.map(({ resourceContent }) => resourceContent),
              })
            }),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage(
                error?.title ?? 'Update action data mapping failed'
              )
              return of(DataMappingActions.bulkUpdateDataMappingsFailure({ elementFlowxUuid }))
            })
          )
        }
      )
    ),
  { functional: true }
)

export const testMapping$ = createEffect(
  (
    actions$ = inject(Actions),
    dataMappingApiService = inject(DataMappingApiService),
    toastMessageService = inject(ToastMessageService)
  ) =>
    actions$.pipe(
      ofType(DataMappingActions.testMapping),
      switchMap(({ mappings, params, mappingType, sliceItems, actionParams, useGraalVM }) =>
        dataMappingApiService
          .testMapping({
            type: mappingType,
            sliceItems,
            actionParams,
            mapping: mappings.map((mapping) => ({
              sourceStringPath: mapping.sourceStringPath,
              targetStringPath: mapping.targetStringPath,
              staticValue: mapping.staticValue,
              expression: mapping.expression,
            })),
            params,
            useGraalVM,
          })
          .pipe(
            map((response) => DataMappingActions.testMappingSuccess({ response })),
            catchError(({ error }: { error: IError }) => {
              toastMessageService.showErrorMessage(error.title ?? 'Test mapping failed')
              return of(DataMappingActions.testMappingFailure())
            })
          )
      )
    ),
  { functional: true }
)

const getNodesSlicesResponse = (
  resourceType: ApplicationResourceType,
  resourceId: string,
  application: ApplicationApiParameters,
  dataMappingApiService: DataMappingApiService,
  toastMessageService: ToastMessageService,
  sliceType?: SliceType
): Observable<
  | ReturnType<typeof DataMappingActions.loadResourceNodesSlicesSuccess>
  | ReturnType<typeof DataMappingActions.loadResourceNodesSlicesFailure>
  | null
> => {
  switch (resourceType) {
    case ApplicationResourceType.Process:
      return dataMappingApiService.getProcessNodesSlices(resourceId, application).pipe(
        map(({ resourceContent: processNodesSlices }) =>
          DataMappingActions.loadResourceNodesSlicesSuccess({
            resourceId,
            resourceNodesSlices: processNodesSlices.nodes,
            dataEntities: (processNodesSlices.dataEntities ?? []).map((e) =>
              ProcessDataModelEntityToUIEntity(e)
            ),
          })
        ),
        catchError(({ error }: { error: IError }) => {
          toastMessageService.showErrorMessage(error.title ?? 'Load process nodes slices failed')
          return of(DataMappingActions.loadResourceNodesSlicesFailure({ resourceId }))
        })
      )
    case ApplicationResourceType.Workflow:
      return dataMappingApiService.getWorkflowNodesSlices(resourceId, application, sliceType).pipe(
        map(({ resourceContent: workflowNodesSlices }) =>
          DataMappingActions.loadResourceNodesSlicesSuccess({
            resourceId,
            sliceType,
            resourceNodesSlices: workflowNodesSlices.nodes,
            dataEntities: (workflowNodesSlices.dataEntities ?? []).map((e) =>
              WorkflowDataModelEntityToUIEntity(e)
            ),
          })
        ),
        catchError(({ error }: { error: IError }) => {
          toastMessageService.showErrorMessage(error.title ?? 'Load workflow nodes slices failed')
          return of(DataMappingActions.loadResourceNodesSlicesFailure({ resourceId }))
        })
      )
    case ApplicationResourceType.NotificationTemplate:
      return dataMappingApiService
        .getNotificationTemplateSlices(resourceId, application, sliceType)
        .pipe(
          map(({ resourceContent: notificationTemplateSlices }) =>
            DataMappingActions.loadResourceNodesSlicesSuccess({
              resourceId,
              sliceType,
              resourceNodesSlices: notificationTemplateSlices.nodes,
              dataEntities: (notificationTemplateSlices.dataEntities ?? []).map((e) =>
                NotificationTemplateDataModelEntityToUIEntity(e)
              ),
            })
          ),
          catchError(({ error }: { error: IError }) => {
            toastMessageService.showErrorMessage(
              error.title ?? 'Load notification template nodes slices failed'
            )
            return of(DataMappingActions.loadResourceNodesSlicesFailure({ resourceId }))
          })
        )
    default:
      toastMessageService.showErrorMessage('Unsupported resource type for loading nodes slices')
      return of(null)
  }
}
